import { AuthError, NetworkError } from '../errors';
import {
  parseJwtToken,
  AUTH_USER_ROLE,
  AUTH_SERVICE,
  COOKIE_NAMES,
  CLIENT_COOKIE_CONFIG,
  setCookie,
  SERVICE_URL_MAP,
  SERVICE_TYPE,
  HTTP_METHODS,
  HTTP_CREDENTIALS_MODE,
  HTTP_STATUS_CODE,
  type AuthTokenResponse,
} from 'utils';

/**
 * Client-side JWT manager singleton for browser environment
 *
 * ## Design Pattern: Singleton
 *
 * Single instance per browser tab prevents parallel token refresh race conditions.
 * Multiple ClientFetchTransformer instances share the same JWT manager instance.
 *
 * ## Responsibilities
 *
 * - Read JWT from document.cookie
 * - Handle token refresh (edge case: SessionKeepAlive interval miss)
 * - Prevent parallel refresh requests via shared pendingAuthRequest
 * - Browser automatically processes Set-Cookie headers from auth-service
 *
 * ## Usage
 *
 * ```typescript
 * const token = await ClientJwtManager.getValidJwtToken();
 * ```
 *
 * Note: Auth primarily handled by authProxy (middleware) and SessionKeepAlive (client).
 * This class is a safety net for edge cases only.
 */
export class ClientJwtManager {
  readonly jwtManagerType = 'ClientJwtManager';

  /**
   * Single instance per browser tab
   */
  private static instance: ClientJwtManager | null = null;

  /**
   * Shared promise for in-flight authentication requests
   * Prevents parallel refreshCustomerToken() or loginAsAnonymous() calls
   */
  private static pendingAuthRequest: Promise<AuthTokenResponse> | null = null;

  /**
   * Private constructor - use static methods instead
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * Get or create the singleton instance
   * @returns ClientJwtManager singleton instance
   */
  private static getInstance(): ClientJwtManager {
    if (!this.instance) {
      this.instance = new ClientJwtManager();
    }

    return this.instance;
  }

  /**
   * Returns the auth service host URL from SERVICE_URL_MAP
   * @returns Auth service host URL
   */
  private getAuthServiceHost(): string {
    return SERVICE_URL_MAP.CLIENT[SERVICE_TYPE.AUTH];
  }

  /**
   * Check if user is authenticated (not anonymous)
   */
  private static isUserAuthenticated(jwt: string | null): boolean {
    const token = parseJwtToken(jwt ?? undefined);

    if (!token) {
      return false;
    }

    return token.role === AUTH_USER_ROLE.USER || token.role === AUTH_USER_ROLE.EMPLOYEE;
  }

  /**
   * Get current JWT token from cookies (may be null/expired)
   */
  static async getJwtToken(): Promise<string | null> {
    const instance = this.getInstance();

    return instance.getCookieValue(COOKIE_NAMES.JWT);
  }

  /**
   * Get valid JWT token with automatic refresh and parallel execution prevention
   *
   * Decision tree:
   * 1. Check document.cookie
   * 2. Fallback: refresh (JWT missing or authenticated) or anonymous login (stale anonymous JWT)
   *
   * Parallel Execution Handling:
   * - First call: Creates pendingAuthRequest and starts auth
   * - Concurrent calls: Wait for same pendingAuthRequest (no new requests)
   * - After completion: Clear promise for next auth cycle
   *
   * @returns Valid JWT token (never null, never expired)
   * @throws Error if refresh/anonymous login fails
   */
  static async getValidJwtToken(): Promise<string> {
    const instance = this.getInstance();

    // Check cookies
    const jwt = instance.getCookieValue(COOKIE_NAMES.JWT);

    if (jwt) {
      const token = parseJwtToken(jwt);

      if (token && !token.isStale) {
        return jwt;
      }
    }

    // Fallback: auth call (edge case - cookies missing or stale)
    if (this.pendingAuthRequest) {
      const { idToken } = await this.pendingAuthRequest;

      return idToken;
    }

    // No auth in progress → start new one.
    // A missing JWT (cookie dropped at exp) may still have a path-scoped refreshToken that the
    // browser sends to the refresh endpoint; anonymous login first would overwrite that session.
    const shouldTryRefresh = !jwt || this.isUserAuthenticated(jwt);

    // Create and cache the auth request promise
    if (shouldTryRefresh) {
      this.pendingAuthRequest = instance.refreshCustomerToken();
    } else {
      this.pendingAuthRequest = instance.loginAsAnonymous();
    }

    try {
      const { idToken } = await this.pendingAuthRequest;

      return idToken;
    } finally {
      // Clear & create a new promise after completion (success or failure)
      this.pendingAuthRequest = null;
    }
  }

  /**
   * Initialize anonymous authentication
   *
   * Browser automatically processes Set-Cookie headers from response
   */
  private async loginAsAnonymous(): Promise<AuthTokenResponse> {
    const url = `${this.getAuthServiceHost()}${AUTH_SERVICE.ENDPOINTS.LOGIN_ANONYMOUS}`;
    let response: Response;

    try {
      response = await fetch(url, {
        method: HTTP_METHODS.POST,
        credentials: HTTP_CREDENTIALS_MODE.SAME_ORIGIN,
      });
    } catch (error) {
      throw new NetworkError(
        'Anonymous login failed due to network error',
        { url },
        error instanceof Error ? error : undefined,
      );
    }

    if (!response.ok) {
      throw new AuthError(`Anonymous login failed: ${response.status}`, response.status, {
        url,
        statusText: response.statusText,
      });
    }

    const { idToken, refreshToken } = await response.json();

    this.storeLastRefreshTime();

    return { idToken, refreshToken };
  }

  /**
   * Refresh customer JWT token with automatic fallback to anonymous login
   * Browser automatically processes Set-Cookie headers from response
   *
   * Fallback logic:
   * - 400: JWT not initialized yet → anonymous login
   * - 401: JWT corrupted or invalid → anonymous login
   */
  private async refreshCustomerToken(): Promise<AuthTokenResponse> {
    const url = `${this.getAuthServiceHost()}${AUTH_SERVICE.ENDPOINTS.REFRESH_CUSTOMER_TOKEN}`;
    let response: Response;

    try {
      response = await fetch(url, {
        method: HTTP_METHODS.POST,
        credentials: HTTP_CREDENTIALS_MODE.SAME_ORIGIN,
      });
    } catch (error) {
      throw new NetworkError(
        'Token refresh failed due to network error',
        { url },
        error instanceof Error ? error : undefined,
      );
    }

    if (!response.ok) {
      if (response.status === HTTP_STATUS_CODE.BAD_REQUEST || response.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
        return this.loginAsAnonymous();
      }

      throw new AuthError(`Token refresh failed: ${response.status}`, response.status, {
        url,
        statusText: response.statusText,
      });
    }

    const { idToken, refreshToken } = await response.json();

    this.storeLastRefreshTime();

    return { idToken, refreshToken };
  }

  /**
   * js-px-lib (editor) writes this after every login/refresh and treats a missing cookie as
   * "auth not initialized", so we keep it in sync.
   */
  private storeLastRefreshTime(): void {
    setCookie(COOKIE_NAMES.LAST_REFRESH_TIME, Date.now().toString(), CLIENT_COOKIE_CONFIG.LAST_REFRESH_TIME);
  }

  /**
   * Parse cookie value from document.cookie
   *
   * document.cookie format: "name1=value1; name2=value2; ..."
   */
  private getCookieValue(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));

    return match ? match[2] : null;
  }

  /**
   * Reset singleton state (for testing only)
   * Clears instance and pending auth request
   *
   * @internal
   */
  static _reset(): void {
    this.instance = null;
    this.pendingAuthRequest = null;
  }
}
